import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { PageHero, SectionHeading } from "../components/Sections";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { EnquiryDialog } from "../components/LeadForm";
import { blogPosts } from "../mock/data";
import { Calendar, ArrowLeft, ArrowRight, Info } from "lucide-react";

export function BlogList() {
  return (
    <div>
      <PageHero title="Knowledge Centre & Blog"
        subtitle="Practical, jargon-free guides on loans, credit and insurance — learn before you borrow."
        crumbs={[{ label: "Blog" }]} />
      <section className="section-pad">
        <div className="container-page">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((b) => (
              <Link to={`/blog/${b.slug}`} key={b.slug} className="group">
                <Card className="svc-card h-full overflow-hidden border">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-semibold uppercase tracking-wide text-primary">{b.category}</span>
                      <span className="flex items-center gap-1 text-muted-foreground"><Calendar className="h-3.5 w-3.5" />{b.date}</span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-semibold leading-snug">{b.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                      Read more <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((b) => b.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const others = blogPosts.filter((b) => b.slug !== slug).slice(0, 3);

  return (
    <div>
      <PageHero title={post.title} crumbs={[{ label: "Blog", to: "/blog" }, { label: post.category }]} />
      <section className="section-pad">
        <div className="container-page grid gap-12 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="flex items-center gap-3 text-xs mb-5">
              <span className="font-semibold uppercase tracking-wide text-primary">{post.category}</span>
              <span className="flex items-center gap-1 text-muted-foreground"><Calendar className="h-3.5 w-3.5" />{post.date}</span>
            </div>
            <img src={post.image} alt={post.title} className="mb-8 w-full rounded-2xl object-cover aspect-[16/9]" />
            <div className="space-y-5 text-[15px] leading-relaxed text-foreground/85">
              {post.body.map((para, i) => <p key={i}>{para}</p>)}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-primary/20 bg-secondary/50 p-4">
              <Info className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                This article is for general information only and is not financial advice. Loan and insurance terms are
                determined by the respective lender/insurer.
              </p>
            </div>
            <div className="mt-8">
              <Button asChild variant="outline"><Link to="/blog"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Blog</Link></Button>
            </div>
          </article>
          <aside>
            <h3 className="font-heading text-lg font-semibold mb-4">More articles</h3>
            <div className="space-y-4">
              {others.map((o) => (
                <Link to={`/blog/${o.slug}`} key={o.slug} className="group flex gap-3">
                  <img src={o.image} alt={o.title} className="h-16 w-20 shrink-0 rounded-lg object-cover" />
                  <div>
                    <span className="text-[11px] font-semibold uppercase text-primary">{o.category}</span>
                    <p className="text-sm font-medium leading-snug group-hover:text-primary line-clamp-2">{o.title}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Card className="mt-6 border p-5 text-center">
              <p className="text-sm font-semibold">Need assistance?</p>
              <div className="mt-3"><EnquiryDialog trigger={<Button size="sm" className="w-full">Check Eligibility</Button>} /></div>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
}
